import React, { useMemo, useState } from "react";
import styled from "styled-components";
import ReactApexChart from "react-apexcharts";
import { useSelector, shallowEqual } from "react-redux";
import { useLocation } from "react-router-dom";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";

const CompletionTimeline = () => {
  const location = useLocation();
  const datasetParam = new URLSearchParams(location.search).get("dataset");
  const [minuteView, setMinuteView] = useState({ open: false, startTs: null, series: [], rangeLabel: "" });

  const { allGroups } = useSelector(
    (state) => ({
      allGroups: state.debrief_schedule?.culture_audit_reports?.response || [],
    }),
    shallowEqual
  );

  const { series, options, hasData, hourlyData, completionDates } = useMemo(() => {
    // Select groups for the chosen dataset if provided; otherwise fallback to all
    const dsNum = datasetParam != null && datasetParam !== "unidentified" ? Number(datasetParam) : null;
    const dsStr = datasetParam != null ? String(datasetParam) : null;

    const matchesDataset = (g) => {
      const candidates = [
        g?.debrief_schedule,
        g?.debrief_schedule_id,
        g?.schedule,
        g?.schedule_id,
        g?.survey_schedule,
        g?.survey_schedule_id,
        g?.survey_event,
        g?.survey_event_id,
      ].filter((v) => v != null);
      if (!candidates.length) return false;
      return candidates.some((v) => {
        if (dsNum != null && !Number.isNaN(dsNum) && Number(v) === dsNum) return true;
        return String(v) === dsStr;
      });
    };

    let groups = Array.isArray(allGroups) ? allGroups : [];
    if (dsStr) {
      const filtered = groups.filter(matchesDataset);
      if (filtered.length > 0) {
        groups = filtered;
      }
    }

    // Determine survey scheduled start as earliest group date (if available)
    const scheduleDates = groups
      .map((g) => (g?.date ? new Date(g.date) : null))
      .filter((d) => d && !isNaN(d.getTime()));

    const scheduledStart = scheduleDates.length
      ? new Date(Math.min(...scheduleDates.map((d) => d.getTime())))
      : null;

    // Collect all completion timestamps
    const completionDates = [];
    for (const g of groups) {
      const rs = Array.isArray(g?.responses) ? g.responses : [];
      for (const r of rs) {
        const ts = r?.created_at || r?.response?.created_at || null;
        const d = ts ? new Date(ts) : null;
        if (d && !isNaN(d.getTime())) {
          completionDates.push(d);
        }
      }
    }

    if (!completionDates.length) {
      return {
        hasData: false,
        series: [],
        options: {},
      };
    }

    // Define start/end hours
    const minCompletion = new Date(
      Math.min(...completionDates.map((d) => d.getTime()))
    );
    const maxCompletion = new Date(
      Math.max(...completionDates.map((d) => d.getTime()))
    );

    const start = scheduledStart && scheduledStart.getTime() <= minCompletion.getTime()
      ? scheduledStart
      : minCompletion;

    // Floor to hour
    const startAligned = new Date(start);
    startAligned.setMinutes(0, 0, 0);
    const endAligned = new Date(maxCompletion);
    endAligned.setMinutes(0, 0, 0);

    // Initialize hour bins
    const ONE_BUCKET = 60 * 60 * 1000;
    const bins = new Map();
    for (let t = startAligned.getTime(); t <= endAligned.getTime(); t += ONE_BUCKET) {
      bins.set(t, 0);
    }

    // Tally completions into hour bins (ignore completions before scheduled start)
    for (const d of completionDates) {
      const h = new Date(d);
      h.setMinutes(0, 0, 0);
      const ht = h.getTime();
      if (ht >= startAligned.getTime() && ht <= endAligned.getTime()) {
        bins.set(ht, (bins.get(ht) || 0) + 1);
      }
    }

    const data = Array.from(bins.entries()).map(([t, c]) => ({ x: t, y: c }));

    const options = {
      chart: {
        type: "bar",
        zoom: { enabled: true, type: "x", autoScaleYaxis: true },
        toolbar: { show: true, tools: { pan: true, zoom: true, zoomin: true, zoomout: true, reset: true } },
        events: {
          dataPointSelection: (event, chartContext, config) => {
            try {
              const idx = config?.dataPointIndex;
              if (idx == null || idx < 0) return;
              const dp = data[idx];
              if (!dp || !dp.x) return;
              const hourStart = new Date(dp.x);
              const hourEnd = new Date(dp.x + 60 * 60 * 1000);

              // Build minute bins within the selected hour
              const minuteBins = new Map();
              for (let t = hourStart.getTime(); t < hourEnd.getTime(); t += 60 * 1000) {
                minuteBins.set(t, 0);
              }
              for (const d of completionDates) {
                if (d >= hourStart && d < hourEnd) {
                  const m = new Date(d);
                  m.setSeconds(0, 0);
                  const mt = m.getTime();
                  if (minuteBins.has(mt)) {
                    minuteBins.set(mt, (minuteBins.get(mt) || 0) + 1);
                  }
                }
              }

              const minuteData = Array.from(minuteBins.entries()).map(([t, c]) => ({ x: t, y: c }));

              setMinuteView({
                open: true,
                startTs: dp.x,
                series: [{ name: "Completions", data: minuteData }],
                rangeLabel: `${hourStart.toLocaleString()} – ${hourEnd.toLocaleTimeString()}`,
              });
            } catch (e) {
              // noop
            }
          },
        },
      },
      xaxis: { type: "datetime", labels: { datetimeUTC: false } },
      yaxis: { title: { text: "Completions" } },
      dataLabels: { enabled: false },
      tooltip: { x: { format: "MMM dd, yyyy HH:mm" } },
      colors: ["#2D70E2"],
      grid: { strokeDashArray: 4 },
      plotOptions: {
        bar: { columnWidth: "65%", borderRadius: 4 },
      },
    };

    const series = [
      {
        name: "Completions",
        data,
      },
    ];

    return { hasData: true, series, options, hourlyData: data, completionDates };
  }, [allGroups, datasetParam]);

  return (
    <Container>
      <AnalyticsBreadcrumb currentComponent="completion-timeline" />
      <Title>Completion Timeline</Title>
      <Subtitle>Counts of survey completions per hour (click a bar for minute detail)</Subtitle>
      {!hasData ? (
        <EmptyState>No completion timestamps found.</EmptyState>
      ) : (
        <ChartWrap>
          <ReactApexChart options={options} series={series} type="bar" height={360} />
        </ChartWrap>
      )}

      {minuteView.open && (
        <ModalOverlay onClick={() => setMinuteView({ open: false, startTs: null, series: [], rangeLabel: "" })}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Minute Distribution</ModalTitle>
              <ModalSubtle>{minuteView.rangeLabel}</ModalSubtle>
              <ModalClose onClick={() => setMinuteView({ open: false, startTs: null, series: [], rangeLabel: "" })} aria-label="Close">✕</ModalClose>
            </ModalHeader>
            <ModalBody>
              <ReactApexChart
                options={{
                  chart: { type: "bar", toolbar: { show: true }, zoom: { enabled: false } },
                  xaxis: { type: "datetime", labels: { datetimeUTC: false } },
                  dataLabels: { enabled: false },
                  colors: ["#27CDA7"],
                  grid: { strokeDashArray: 4 },
                  plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } },
                  tooltip: { x: { format: "MMM dd, yyyy HH:mm" } },
                }}
                series={minuteView.series}
                type="bar"
                height={280}
              />
            </ModalBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default CompletionTimeline;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
`;

const Subtitle = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #64748b;
`;

const ChartWrap = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  padding: 16px;
`;

const EmptyState = styled.div`
  padding: 24px;
  text-align: center;
  color: #64748b;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 12px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  width: 720px;
  max-width: calc(100vw - 40px);
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.25);
  box-shadow: 0 10px 30px rgba(0,0,0,0.12);
  padding: 16px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 8px;
`;

const ModalTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
`;

const ModalSubtle = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const ModalClose = styled.button`
  margin-left: auto;
  background: transparent;
  border: none;
  color: #334155;
  font-size: 16px;
  cursor: pointer;
`;

const ModalBody = styled.div`
  margin-top: 8px;
`;


